import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Grid,
  Divider,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  alpha,
  Tooltip,
  ListItemIcon,
  ListItemText,
  FormHelperText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PointOfSale as PointOfSaleIcon,
  Balance as BalanceIcon,
  Warehouse as WarehouseIcon,
  Image as ImageIcon,
  Info as InfoIcon,
  CloudUpload as CloudUploadIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { branchesApi } from '../../services/api';
import apiService from '../../services/apiService';
import { BranchFormData } from '../../types/branch';
import { getInvoiceMenuItems, getIconForInvoiceType } from '../dashboard/DynamicInvoiceMenu';

const initialFormData: BranchFormData = {
  name: '',
  address: '',
  phone: '',
  email: '',
  manager: '',
  status: 'active',
  sequentialNumber: undefined,
  branchId: undefined,
  defaultPosPage: '',
  balanceVoucherTemplateId: '',
  warehouses: [],
  logo: '',
  establishmentInfo: '',
  timezone: 'Asia/Riyadh'
};

const BranchForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<BranchFormData>(initialFormData);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [invoicePages, setInvoicePages] = useState<Array<{id: string, to: string, icon: React.ReactNode, text: string}>>([]);
  const [loadingInvoicePages, setLoadingInvoicePages] = useState<boolean>(false);
  const [balanceTemplates, setBalanceTemplates] = useState<Array<{id: string, name: string}>>([]);
  const [loadingBalanceTemplates, setLoadingBalanceTemplates] = useState<boolean>(false);
  const [balanceTemplatesError, setBalanceTemplatesError] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<Array<{_id: string, name: string}>>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState<boolean>(false);
  const [logoPreview, setLogoPreview] = useState<string>('');

  // تحميل صفحات الفواتير
  useEffect(() => {
    const loadInvoicePages = async () => {
      try {
        setLoadingInvoicePages(true);
        const items = await getInvoiceMenuItems();
        // تصفية العناصر التي لديها معرف صالح
        const validItems = items.filter(item => item.id) as Array<{id: string, to: string, icon: React.ReactNode, text: string}>;
        setInvoicePages(validItems);
      } catch (error) {
        console.error('فشل في تحميل صفحات الفواتير:', error);
      } finally {
        setLoadingInvoicePages(false);
      }
    };
    
    loadInvoicePages();
  }, []);
  
  // تحميل قوالب سندات الموازنة
  useEffect(() => {
    const loadBalanceTemplates = async () => {
      try {
        setLoadingBalanceTemplates(true);
        setBalanceTemplatesError(null);
        
        // جلب قوالب السندات المتخصصة من نوع "journal" (سند موازنة)
        const response = await apiService.get('/finance/specialized-vouchers/templates');
        
        if (response && Array.isArray(response)) {
          // تصفية القوالب للحصول على قوالب سندات الموازنة فقط
          const journalTemplates = response
            .filter(template => template.voucherType === 'journal')
            .map(template => ({
              id: template._id || template.id,
              name: template.name
            }));
            
          setBalanceTemplates(journalTemplates);
          console.log('تم تحميل قوالب سندات الموازنة:', journalTemplates);
        } else {
          console.error('تنسيق الاستجابة غير متوقع:', response);
          setBalanceTemplatesError('حدث خطأ أثناء تحميل قوالب سندات الموازنة');
        }
      } catch (error) {
        console.error('فشل في تحميل قوالب سندات الموازنة:', error);
        setBalanceTemplatesError('فشل في تحميل قوالب سندات الموازنة');
      } finally {
        setLoadingBalanceTemplates(false);
      }
    };
    
    loadBalanceTemplates();
  }, []);

  // تحميل المستودعات
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        setLoadingWarehouses(true);
        const response: any = await apiService.get('/warehouses');
        if (response && response.warehouses) {
          setWarehouses(response.warehouses.filter((w: any) => w.status === 'active'));
        }
      } catch (error) {
        console.error('فشل في تحميل المستودعات:', error);
      } finally {
        setLoadingWarehouses(false);
      }
    };
    
    loadWarehouses();
  }, []);

  useEffect(() => {
    const fetchBranch = async () => {
      if (isEditMode && id) {
        try {
          setLoading(true);
          const branch = await branchesApi.getBranch(id);
          setFormData({
            name: branch.name,
            address: branch.address || '',
            phone: branch.phone || '',
            email: branch.email || '',
            manager: branch.manager || '',
            status: branch.status,
            sequentialNumber: branch.sequentialNumber,
            branchId: branch.branchId,
            defaultPosPage: branch.defaultPosPage || '',
            balanceVoucherTemplateId: branch.balanceVoucherTemplateId || '',
            warehouses: branch.warehouses || [],
            logo: branch.logo || '',
            establishmentInfo: branch.establishmentInfo || '',
            timezone: branch.timezone || 'Asia/Riyadh'
          });
          if (branch.logo) {
            setLogoPreview(branch.logo);
          }
        } catch (err) {
          setError((err as Error).message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchBranch();
  }, [id, isEditMode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    if (!touched[name]) {
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // التحقق من نوع الملف
      if (!file.type.startsWith('image/')) {
        setError('يرجى اختيار ملف صورة صحيح');
        return;
      }
      
      // التحقق من حجم الملف (أقل من 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
        return;
      }

      // قراءة الملف وتحويله إلى Base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFormData(prev => ({
          ...prev,
          logo: base64String
        }));
        setLogoPreview(base64String);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo: ''
    }));
    setLogoPreview('');
  };

  const validateForm = (): boolean => {
    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
    
    setTouched(allTouched);
    
    // Basic validation
    return !!formData.name.trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      // Log what we're submitting to help with debugging
      console.log('Submitting branch data:', formData);
      console.log('Default POS Page selected:', formData.defaultPosPage || 'None');
      
      // Ensure all fields are properly formatted
      const formattedData: BranchFormData = {
        name: formData.name.trim(),
        address: formData.address?.trim() || '',
        phone: formData.phone?.trim() || '',
        email: formData.email?.trim() || '',
        manager: formData.manager?.trim() || '',
        status: formData.status === 'active' ? 'active' : 'inactive',
        defaultPosPage: formData.defaultPosPage || '',
        balanceVoucherTemplateId: formData.balanceVoucherTemplateId || '',
        warehouses: formData.warehouses || [],
        logo: formData.logo || '',
        establishmentInfo: formData.establishmentInfo?.trim() || '',
        timezone: formData.timezone || 'Asia/Riyadh'
      };
      
      console.log('Formatted data to be sent to server:', formattedData);
      
      // Preserve sequentialNumber and branchId when editing
      if (isEditMode) {
        if (formData.sequentialNumber !== undefined) {
          formattedData.sequentialNumber = formData.sequentialNumber;
        }
        if (formData.branchId !== undefined) {
          formattedData.branchId = formData.branchId;
        }
      }
      
      if (isEditMode && id) {
        await branchesApi.updateBranch(id, formattedData);
      } else {
        await branchesApi.createBranch(formattedData);
      }
      
      // Success! Navigate back to branches list
      navigate('/settings/branches');
    } catch (err) {
      console.error('Error submitting branch form:', err);
      
      // Display a more user-friendly error message
      if (err instanceof Error) {
        setError(err.message || 'حدث خطأ أثناء حفظ البيانات');
      } else {
        setError('حدث خطأ غير متوقع في النظام');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, #eef2f6)', pb: 5 }}>
      {/* Header Section */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          mb: 4,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: isEditMode ?
              'linear-gradient(to right, #ff9800, #f57c00)' :
              'linear-gradient(to right, #4caf50, #2e7d32)'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
            <IconButton
              onClick={() => navigate('/settings/branches')}
              sx={{
                bgcolor: alpha('#f0f0f0', 0.8),
                '&:hover': { bgcolor: alpha('#e0e0e0', 0.9) },
                mr: 1
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: isEditMode ? 'warning.dark' : 'success.dark',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}
              >
                <BusinessIcon
                  fontSize="large"
                  sx={{
                    color: isEditMode ? 'warning.main' : 'success.main',
                    background: isEditMode ?
                      alpha('#ff9800', 0.1) :
                      alpha('#4caf50', 0.1),
                    p: 1,
                    borderRadius: 2,
                    boxShadow: `0 4px 12px ${isEditMode ?
                      'rgba(255, 152, 0, 0.2)' :
                      'rgba(76, 175, 80, 0.2)'}`
                  }}
                />
                {isEditMode ? 'تعديل الفرع' : 'إضافة فرع جديد'}
              </Typography>
              
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  mt: 1.5,
                  maxWidth: 600,
                  fontSize: '0.95rem'
                }}
              >
                {isEditMode
                  ? 'قم بتعديل بيانات الفرع وحفظ التغييرات عند الانتهاء'
                  : 'قم بإدخال بيانات الفرع الجديد واضغط على زر الإضافة عند الانتهاء'
                }
              </Typography>
            </Box>
          </Box>
          
          {/* Date and Day Display */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05), rgba(33, 150, 243, 0.15))',
              px: 2.5,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
              border: '1px solid rgba(33, 150, 243, 0.1)'
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {new Date().toLocaleDateString('ar-SA', { weekday: 'long' })}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {new Date().toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Form Section */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 800,
            width: '100%',
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
            position: 'relative'
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={40} />
              <Typography sx={{ ml: 2, color: 'text.secondary' }}>
                جاري تحميل بيانات الفرع...
              </Typography>
            </Box>
          ) : (
            <>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(244, 67, 54, 0.15)'
                  }}
                >
                  {error}
                </Alert>
              )}
              
              {/* Show Branch ID and Sequential Number for editing mode */}
              {isEditMode && (formData.sequentialNumber !== undefined || formData.branchId !== undefined) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 3,
                    px: 2,
                    py: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(33, 150, 243, 0.05)',
                    border: '1px dashed rgba(33, 150, 243, 0.3)',
                  }}
                >
                  {formData.sequentialNumber !== undefined && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        الرقم التسلسلي
                      </Typography>
                      <Chip
                        label={formData.sequentialNumber}
                        color="primary"
                        sx={{ fontWeight: 'bold', minWidth: '80px' }}
                      />
                    </Box>
                  )}
                  
                  {formData.branchId !== undefined && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        معرف الفرع
                      </Typography>
                      <Chip
                        label={formData.branchId}
                        color="secondary"
                        sx={{ fontWeight: 'bold', minWidth: '80px' }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BusinessIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        بيانات الفرع الأساسية
                      </Typography>
                    </Box>
                    <TextField
                      required
                      fullWidth
                      dir="rtl"
                      label="اسم الفرع"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={submitting}
                      error={touched.name && !formData.name.trim()}
                      helperText={touched.name && !formData.name.trim() ? 'اسم الفرع مطلوب' : ''}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: touched.name && !formData.name.trim()
                            ? '0 0 0 2px rgba(244, 67, 54, 0.2)'
                            : '0 2px 6px rgba(0,0,0,0.04)',
                          fontWeight: 500,
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1, opacity: 0.6 }} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationOnIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'error.main' }}>
                        العنوان
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      dir="rtl"
                      label="العنوان"
                      name="address"
                      value={formData.address || ''}
                      onChange={handleInputChange}
                      disabled={submitting}
                      multiline
                      rows={3}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PersonIcon sx={{ color: 'warning.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                        المدير
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      dir="rtl"
                      label="اسم المدير"
                      name="manager"
                      value={formData.manager || ''}
                      onChange={handleInputChange}
                      disabled={submitting}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        رقم الهاتف
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      dir="rtl"
                      label="رقم الهاتف"
                      name="phone"
                      value={formData.phone || ''}
                      onChange={handleInputChange}
                      disabled={submitting}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ color: 'info.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                        البريد الإلكتروني
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      dir="rtl"
                      label="البريد الإلكتروني"
                      name="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={handleInputChange}
                      disabled={submitting}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1, opacity: 0.6 }} />
                  </Grid>

                  {/* نقطة البيع الافتراضي */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PointOfSaleIcon sx={{ color: 'info.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                        نقطة البيع الافتراضي
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel id="default-pos-page-label">صفحة الفواتير الافتراضية</InputLabel>
                      <Select
                        labelId="default-pos-page-label"
                        id="defaultPosPage"
                        name="defaultPosPage"
                        value={formData.defaultPosPage || ''}
                        label="نقطة البيع الافتراضي"
                        onChange={handleSelectChange}
                        disabled={submitting || loadingInvoicePages}
                        dir="rtl"
                        sx={{
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <MenuItem value="">
                          <Box sx={{ opacity: 0.7, fontStyle: 'italic' }}>اختر صفحة الفواتير الافتراضية</Box>
                        </MenuItem>
                        
                        {loadingInvoicePages ? (
                          <MenuItem disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                              <CircularProgress size={20} />
                              <Typography>جاري تحميل صفحات الفواتير...</Typography>
                            </Box>
                          </MenuItem>
                        ) : (
                          invoicePages.map((page) => (
                            <MenuItem key={page.id} value={page.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  {page.icon}
                                </ListItemIcon>
                                <ListItemText primary={page.text} />
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {loadingInvoicePages && (
                        <CircularProgress size={20} sx={{ mt: 1, mr: 1 }} />
                      )}
                    </FormControl>
                  </Grid>
                  {/* قالب سند الموازنة */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BalanceIcon sx={{ color: 'secondary.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        قالب سند الموازنة
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel id="balance-voucher-template-label">قالب سند الموازنة</InputLabel>
                      <Select
                        labelId="balance-voucher-template-label"
                        id="balanceVoucherTemplateId"
                        name="balanceVoucherTemplateId"
                        value={formData.balanceVoucherTemplateId || ''}
                        label="قالب سند الموازنة"
                        onChange={handleSelectChange}
                        disabled={submitting || loadingBalanceTemplates}
                        dir="rtl"
                        sx={{
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <MenuItem value="">
                          <Box sx={{ opacity: 0.7, fontStyle: 'italic' }}>اختر قالب سند الموازنة</Box>
                        </MenuItem>
                        
                        {loadingBalanceTemplates ? (
                          <MenuItem disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                              <CircularProgress size={20} />
                              <Typography>جاري تحميل قوالب سندات الموازنة...</Typography>
                            </Box>
                          </MenuItem>
                        ) : (
                          balanceTemplates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <BalanceIcon />
                                </ListItemIcon>
                                <ListItemText primary={template.name} />
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {loadingBalanceTemplates && (
                        <CircularProgress size={20} sx={{ mt: 1, mr: 1 }} />
                      )}
                      {balanceTemplatesError && (
                        <FormHelperText error>{balanceTemplatesError}</FormHelperText>
                      )}
                      <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                        سيظهر قالب سند الموازنة في القائمة العلوية للنظام عند تعيينه
                      </Typography>
                    </FormControl>
                  </Grid>

                  {/* مستودعات الفرع */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <WarehouseIcon sx={{ color: 'primary.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        مستودعات الفرع
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel id="warehouses-label">اختر المستودعات</InputLabel>
                      <Select
                        labelId="warehouses-label"
                        id="warehouses"
                        name="warehouses"
                        multiple
                        value={formData.warehouses || []}
                        label="اختر المستودعات"
                        onChange={(e) => {
                          const value = e.target.value as string[];
                          setFormData(prev => ({
                            ...prev,
                            warehouses: value
                          }));
                        }}
                        disabled={submitting || loadingWarehouses}
                        dir="rtl"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => {
                              const warehouse = warehouses.find(w => w._id === value);
                              return (
                                <Chip
                                  key={value}
                                  label={warehouse?.name || value}
                                  size="small"
                                  sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
                                />
                              );
                            })}
                          </Box>
                        )}
                        sx={{
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        {loadingWarehouses ? (
                          <MenuItem disabled>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                              <CircularProgress size={20} />
                              <Typography>جاري تحميل المستودعات...</Typography>
                            </Box>
                          </MenuItem>
                        ) : (
                          warehouses.map((warehouse) => (
                            <MenuItem key={warehouse._id} value={warehouse._id}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <WarehouseIcon />
                                </ListItemIcon>
                                <ListItemText primary={warehouse.name} />
                              </Box>
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                        اختر المستودعات التي يمكن للفرع الوصول إليها في طلبات التحويل
                      </Typography>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {formData.status === 'active' ? (
                        <CheckCircleIcon sx={{ color: 'success.main', mr: 1, fontSize: 18 }} />
                      ) : (
                        <CancelIcon sx={{ color: 'error.main', mr: 1, fontSize: 18 }} />
                      )}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: formData.status === 'active' ? 'success.main' : 'error.main'
                        }}
                      >
                        حالة الفرع
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel id="status-label">الحالة</InputLabel>
                      <Select
                        labelId="status-label"
                        id="status"
                        name="status"
                        value={formData.status}
                        label="الحالة"
                        onChange={handleSelectChange}
                        disabled={submitting}
                        dir="rtl"
                        sx={{
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <MenuItem value="active">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              size="small"
                              label="نشط"
                              sx={{
                                bgcolor: 'rgba(76, 175, 80, 0.1)',
                                color: 'success.dark',
                                fontWeight: 600,
                                borderRadius: 1
                              }}
                            />
                            فرع نشط ويعمل بشكل طبيعي
                          </Box>
                        </MenuItem>
                        <MenuItem value="inactive">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              size="small"
                              label="غير نشط"
                              sx={{
                                bgcolor: 'rgba(244, 67, 54, 0.1)',
                                color: 'error.dark',
                                fontWeight: 600,
                                borderRadius: 1
                              }}
                            />
                            فرع متوقف مؤقتاً عن العمل
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* المنطقة الزمنية */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ScheduleIcon sx={{ color: 'info.main', mr: 1, fontSize: 18 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                        المنطقة الزمنية
                      </Typography>
                    </Box>
                    <FormControl fullWidth>
                      <InputLabel id="timezone-label">المنطقة الزمنية</InputLabel>
                      <Select
                        labelId="timezone-label"
                        id="timezone"
                        name="timezone"
                        value={formData.timezone || 'Asia/Riyadh'}
                        label="المنطقة الزمنية"
                        onChange={handleSelectChange}
                        disabled={submitting}
                        dir="rtl"
                        sx={{
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <MenuItem value="Asia/Riyadh">الرياض (GMT+3)</MenuItem>
                        <MenuItem value="Asia/Dubai">دبي (GMT+4)</MenuItem>
                        <MenuItem value="Asia/Kuwait">الكويت (GMT+3)</MenuItem>
                        <MenuItem value="Asia/Bahrain">البحرين (GMT+3)</MenuItem>
                        <MenuItem value="Asia/Qatar">قطر (GMT+3)</MenuItem>
                        <MenuItem value="Asia/Muscat">مسقط (GMT+4)</MenuItem>
                        <MenuItem value="Africa/Cairo">القاهرة (GMT+2)</MenuItem>
                        <MenuItem value="Asia/Baghdad">بغداد (GMT+3)</MenuItem>
                        <MenuItem value="Asia/Amman">عمان (GMT+3)</MenuItem>
                        <MenuItem value="Asia/Beirut">بيروت (GMT+2)</MenuItem>
                        <MenuItem value="Asia/Damascus">دمشق (GMT+3)</MenuItem>
                        <MenuItem value="Africa/Casablanca">الدار البيضاء (GMT+1)</MenuItem>
                        <MenuItem value="Africa/Tunis">تونس (GMT+1)</MenuItem>
                        <MenuItem value="Africa/Algiers">الجزائر (GMT+1)</MenuItem>
                        <MenuItem value="Europe/Istanbul">إسطنبول (GMT+3)</MenuItem>
                        <MenuItem value="UTC">التوقيت العالمي المنسق (UTC)</MenuItem>
                      </Select>
                      <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                        سيتم استخدام هذه المنطقة الزمنية لعرض التواريخ والأوقات في هذا الفرع
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 2, opacity: 0.6 }} />
                  </Grid>

                  {/* قسم الشعار ومعلومات المنشأة */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <ImageIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        شعار الفرع
                      </Typography>
                    </Box>
                    
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      p: 3,
                      borderRadius: 2,
                      bgcolor: alpha('#f5f5f5', 0.5),
                      border: '1px dashed rgba(0,0,0,0.12)'
                    }}>
                      {logoPreview ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Box
                            component="img"
                            src={logoPreview}
                            alt="شعار الفرع"
                            sx={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              objectFit: 'contain',
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              bgcolor: 'white',
                              p: 2
                            }}
                          />
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleRemoveLogo}
                            disabled={submitting}
                            sx={{ borderRadius: 2 }}
                          >
                            إزالة الشعار
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center' }}>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            type="file"
                            onChange={handleLogoChange}
                            disabled={submitting}
                          />
                          <label htmlFor="logo-upload">
                            <Button
                              variant="contained"
                              component="span"
                              startIcon={<CloudUploadIcon />}
                              disabled={submitting}
                              sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                bgcolor: 'primary.main',
                                '&:hover': {
                                  bgcolor: 'primary.dark',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)'
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              اختر شعار الفرع
                            </Button>
                          </label>
                          <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                            يُفضل استخدام صورة بصيغة PNG أو JPG بحجم أقل من 2 ميجابايت
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon sx={{ color: 'secondary.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'secondary.main' }}>
                        معلومات المنشأة
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      dir="rtl"
                      label="معلومات المنشأة"
                      name="establishmentInfo"
                      value={formData.establishmentInfo || ''}
                      onChange={handleInputChange}
                      disabled={submitting}
                      multiline
                      rows={4}
                      placeholder="أدخل معلومات المنشأة (على الأقل 4 أسطر)..."
                      helperText="يمكنك إضافة معلومات تفصيلية عن المنشأة مثل: نوع النشاط، السجل التجاري، الرقم الضريبي، العنوان التفصيلي، وغيرها"
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.8),
                          boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                          },
                          transition: 'all 0.2s',
                          minHeight: '120px'
                        }
                      }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2, opacity: 0.6 }} />
                    
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mt: 2
                    }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/settings/branches')}
                        disabled={submitting}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.2,
                          border: '1px solid rgba(0,0,0,0.12)',
                          color: 'text.secondary',
                          bgcolor: alpha('#f5f5f5', 0.5),
                          '&:hover': {
                            bgcolor: alpha('#f0f0f0', 0.8),
                            borderColor: 'rgba(0,0,0,0.2)'
                          }
                        }}
                      >
                        العودة للقائمة
                      </Button>
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="حفظ كمسودة" arrow>
                          <Button
                            type="button"
                            variant="outlined"
                            disabled={submitting || !formData.name.trim()}
                            sx={{
                              borderRadius: 2,
                              px: 2,
                              py: 1.2,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                                bgcolor: alpha(('#e3f2fd'), 0.5),
                              }
                            }}
                          >
                            حفظ كمسودة
                          </Button>
                        </Tooltip>
                        
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={submitting || !formData.name.trim()}
                          sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.2,
                            bgcolor: isEditMode ? 'warning.main' : 'success.main',
                            boxShadow: isEditMode
                              ? '0 4px 12px rgba(255, 152, 0, 0.3)'
                              : '0 4px 12px rgba(76, 175, 80, 0.3)',
                            '&:hover': {
                              bgcolor: isEditMode ? 'warning.dark' : 'success.dark',
                              transform: 'translateY(-2px)',
                              boxShadow: isEditMode
                                ? '0 6px 16px rgba(255, 152, 0, 0.4)'
                                : '0 6px 16px rgba(76, 175, 80, 0.4)'
                            },
                            transition: 'all 0.2s'
                          }}
                        >
                          {submitting ? (
                            <>
                              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                              جاري الحفظ...
                            </>
                          ) : (
                            isEditMode ? 'حفظ التغييرات' : 'إضافة الفرع'
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default BranchForm;